import React, { useState, useEffect } from 'react';
import { api, ApiError } from './api';
import { EstadoError } from './EstadoError';
import { BRANDING_POR_DEFECTO, prepararArchivosBranding } from './branding';

export default function Configuracion({ branding = BRANDING_POR_DEFECTO, onBrandingChange }) {
  const [config, setConfig] = useState({
    nombre_negocio: '', nombre_app: '', razon_social: '', cuit: '', condicion_iva: 'Responsable Inscripto', ingresos_brutos: '', inicio_actividades: '', direccion: '', telefono: '',
    punto_venta: 1, afip_configurado: false, afip_ambiente: 'homologación',
    // Mercado Pago
    mp_alias: '', mp_cbu: '', mp_titular: '', mp_qr_imagen_url: '',
    // Transferencia bancaria
    banco_alias: '', banco_cbu: '', banco_titular: '', banco_nombre: '',
    descuento_max_cajero: 5,
  });
  const [errorCarga, setErrorCarga] = useState('');
  const [archivoLogo, setArchivoLogo] = useState(null);
  const [previewLogo, setPreviewLogo] = useState(null);
  const [procesandoLogo, setProcesandoLogo] = useState(false);

  const cargarConfig = async () => {
    setErrorCarga('');
    try {
      const data = await api.get('/api/configuracion');
      setConfig({
        nombre_negocio: data.nombre_negocio || '',
        nombre_app: data.nombre_app || data.nombre_negocio || 'Casa Alessio POS',
        razon_social: data.razon_social || '',
        cuit: data.cuit || '',
        condicion_iva: data.condicion_iva || 'Responsable Inscripto',
        ingresos_brutos: data.ingresos_brutos || '',
        inicio_actividades: data.inicio_actividades ? String(data.inicio_actividades).slice(0, 10) : '',
        direccion: data.direccion || '',
        telefono: data.telefono || '',
        punto_venta: data.punto_venta || 1,
        afip_configurado: Boolean(data.afip_configurado),
        afip_ambiente: data.afip_ambiente || 'homologación',
        mp_alias: data.mp_alias || '',
        mp_cbu: data.mp_cbu || '',
        mp_titular: data.mp_titular || '',
        mp_qr_imagen_url: data.mp_qr_imagen_url || '',
        banco_alias: data.banco_alias || '',
        banco_cbu: data.banco_cbu || '',
        banco_titular: data.banco_titular || '',
        banco_nombre: data.banco_nombre || '',
        descuento_max_cajero: data.descuento_max_cajero ?? 5,
      });
    } catch (err) {
      setErrorCarga(err instanceof ApiError ? err.mensaje : 'No se pudo cargar la configuración.');
    }
  };

  useEffect(() => { cargarConfig(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Forma funcional: usa el estado más reciente (prev) en vez del config
    // del closure, que puede estar desactualizado si el usuario edita varios
    // campos rápido. Esto causaba que ciertos campos (ej. mp_alias) se
    // pisaran con el valor anterior al guardar.
    setConfig(prev => ({ ...prev, [name]: value }));
  };

  const guardarConfiguracion = async () => {
    try {
      const respuesta = await api.put('/api/configuracion', config);
      onBrandingChange?.(respuesta.branding || branding);
      alert('✅ Configuración guardada correctamente.');
    } catch (err) {
      alert('❌ ' + (err instanceof ApiError ? err.mensaje : 'Error de conexión al guardar.'));
    }
  };

  const seleccionarLogo = async (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    setProcesandoLogo(true);
    try {
      const preparado = await prepararArchivosBranding(file);
      if (previewLogo) URL.revokeObjectURL(previewLogo);
      setArchivoLogo(preparado);
      setPreviewLogo(preparado.previewUrl);
    } catch (err) {
      e.target.value = '';
      alert('❌ ' + (err.message || 'No se pudo procesar la imagen.'));
    } finally {
      setProcesandoLogo(false);
    }
  };

  const guardarLogo = async () => {
    if (!archivoLogo) return alert('Seleccioná primero una imagen.');
    setProcesandoLogo(true);
    try {
      const form = new FormData();
      const nombreArchivo = (base, blob) => `${base}.${blob.type === 'image/jpeg' ? 'jpg' : 'png'}`;
      form.append('logo', archivoLogo.logo, nombreArchivo('logo', archivoLogo.logo));
      form.append('icono192', archivoLogo.icono192, nombreArchivo('icono-192', archivoLogo.icono192));
      form.append('icono512', archivoLogo.icono512, nombreArchivo('icono-512', archivoLogo.icono512));
      form.append('favicon', archivoLogo.favicon, nombreArchivo('favicon', archivoLogo.favicon));
      const respuesta = await api.upload('/api/configuracion/branding', form);
      if (previewLogo) URL.revokeObjectURL(previewLogo);
      setPreviewLogo(null);
      setArchivoLogo(null);
      onBrandingChange?.(respuesta.branding);
      alert('✅ Logo e íconos actualizados. Para cambiar el ícono de una app ya instalada, eliminá el acceso y volvé a instalarlo.');
    } catch (err) {
      alert('❌ ' + (err instanceof ApiError ? err.mensaje : 'No se pudo guardar la identidad visual.'));
    } finally {
      setProcesandoLogo(false);
    }
  };

  const restaurarLogo = async () => {
    if (!window.confirm('¿Restaurar el logo e íconos predeterminados?')) return;
    setProcesandoLogo(true);
    try {
      const respuesta = await api.delete('/api/configuracion/branding');
      if (previewLogo) URL.revokeObjectURL(previewLogo);
      setPreviewLogo(null);
      setArchivoLogo(null);
      onBrandingChange?.(respuesta.branding);
      alert('✅ Identidad visual predeterminada restaurada.');
    } catch (err) {
      alert('❌ ' + (err instanceof ApiError ? err.mensaje : 'No se pudo restaurar la identidad visual.'));
    } finally {
      setProcesandoLogo(false);
    }
  };

  if (errorCarga) {
    return <EstadoError mensaje={errorCarga} onReintentar={cargarConfig} />;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '700px', margin: '0 auto', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', minHeight: '80vh' }}>
      <h2 style={{ color: '#0f172a', borderBottom: '2px solid #e2e8f0', paddingBottom: '10px', marginTop: 0 }}>⚙️ Configuración del Sistema</h2>
      
      <div style={{ display: 'grid', gap: '15px', marginTop: '20px' }}>
        <div><label style={labelStyle}>Nombre comercial</label><input type="text" name="nombre_negocio" value={config.nombre_negocio} onChange={handleChange} style={inputStyle} /></div>
        <div><label style={labelStyle}>Nombre de la aplicación</label><input type="text" name="nombre_app" value={config.nombre_app} onChange={handleChange} style={inputStyle} maxLength={80} placeholder="Casa Alessio POS" /><small style={{ color: '#64748b', display: 'block', marginTop: 4 }}>Se usa en la pestaña del navegador y al instalar la app en el teléfono o la PC.</small></div>

        <div style={{ padding: '18px', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '10px' }}>
          <h3 style={{ margin: '0 0 8px', color: '#0f172a' }}>🖼️ Identidad visual</h3>
          <p style={{ margin: '0 0 14px', color: '#64748b', fontSize: 13 }}>Subí una imagen PNG, JPG o WebP de hasta 2 MB. Se adapta automáticamente para Dashboard, reportes, tickets, favicon e íconos de instalación.</p>
          <div style={{ minHeight: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', border: '1px dashed #94a3b8', borderRadius: 8, padding: 12, marginBottom: 12 }}>
            {(previewLogo || branding?.logo_url) ? <img src={previewLogo || branding.logo_url} alt="Vista previa del logo" style={{ maxWidth: '100%', maxHeight: 110, objectFit: 'contain' }} /> : <div style={{ textAlign: 'center', color: '#64748b' }}><strong>{config.nombre_negocio || 'Casa Alessio'}</strong><br/><small>Sin logo personalizado</small></div>}
          </div>
          <input type="file" accept="image/png,image/jpeg,image/webp" onChange={seleccionarLogo} disabled={procesandoLogo} style={{ width: '100%', marginBottom: 12 }} />
          {archivoLogo?.totalBytes ? <small style={{ color: '#475569', display: 'block', marginBottom: 10 }}>Carga preparada: {(archivoLogo.totalBytes / 1024).toFixed(0)} KB.</small> : null}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" onClick={guardarLogo} disabled={!archivoLogo || procesandoLogo} style={{ padding: '10px 14px', border: 0, borderRadius: 7, background: !archivoLogo || procesandoLogo ? '#94a3b8' : '#2563eb', color: 'white', fontWeight: 700, cursor: !archivoLogo || procesandoLogo ? 'not-allowed' : 'pointer' }}>{procesandoLogo ? 'Procesando…' : 'Guardar logo e íconos'}</button>
            <button type="button" onClick={restaurarLogo} disabled={procesandoLogo || !branding?.personalizado} style={{ padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: 7, background: 'white', color: '#475569', fontWeight: 700, cursor: procesandoLogo || !branding?.personalizado ? 'not-allowed' : 'pointer' }}>Restaurar predeterminado</button>
          </div>
          <small style={{ display: 'block', marginTop: 10, color: '#64748b' }}>Para un buen ícono, usá una imagen cuadrada o con espacio alrededor del logo. El archivo se guarda en la base de datos y no se pierde al actualizar.</small>
        </div>

        <div><label style={labelStyle}>Razón social</label><input type="text" name="razon_social" value={config.razon_social} onChange={handleChange} style={inputStyle} /></div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div><label style={labelStyle}>CUIT (Sin guiones)</label><input type="text" inputMode="numeric" pattern="[0-9]*" name="cuit" value={config.cuit} onChange={handleChange} style={inputStyle} placeholder="Ej: 20123456789" /></div>
            <div><label style={labelStyle}>Condición frente al IVA</label><select name="condicion_iva" value={config.condicion_iva} onChange={handleChange} style={inputStyle}><option>Responsable Inscripto</option><option>Monotributo</option><option>Exento</option><option>Consumidor Final</option></select></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div><label style={labelStyle}>Ingresos Brutos</label><input type="text" name="ingresos_brutos" value={config.ingresos_brutos} onChange={handleChange} style={inputStyle} /></div>
            <div><label style={labelStyle}>Inicio de actividades</label><input type="date" name="inicio_actividades" value={config.inicio_actividades} onChange={handleChange} style={inputStyle} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px' }}>
            <div><label style={labelStyle}>Dirección Fiscal</label><input type="text" name="direccion" value={config.direccion} onChange={handleChange} style={inputStyle} /></div>
            <div><label style={labelStyle}>Teléfono</label><input type="text" name="telefono" value={config.telefono} onChange={handleChange} style={inputStyle} /></div>
        </div>
        
        {/* EL NUEVO CASILLERO DEL PUNTO DE VENTA */}
        <div style={{ backgroundColor: '#fefce8', padding: '15px', borderRadius: '8px', border: '1px solid #fde047' }}>
          <label style={{...labelStyle, color: '#854d0e', fontSize: '15px'}}>Punto de Venta ARCA (Web Services)</label>
          <input type="number" name="punto_venta" value={config.punto_venta} onChange={handleChange} style={{...inputStyle, borderColor: '#fde047', fontWeight: 'bold', fontSize: '18px'}} placeholder="Ej: 5" />
          <small style={{ color: '#a16207', display: 'block', marginTop: '5px' }}>⚠️ Ingresá solo el número sin ceros a la izquierda (Ej: Si creaste el 00005, escribí <b>5</b>).</small>
        </div>


        <div style={{ backgroundColor: '#fff7ed', padding: '15px', borderRadius: '8px', border: '1px solid #fed7aa' }}>
          <label style={{...labelStyle, color: '#9a3412', fontSize: '15px'}}>Límite de descuento para cajeros</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input type="number" min="0" max="100" step="0.5" name="descuento_max_cajero" value={config.descuento_max_cajero} onChange={handleChange}
              style={{...inputStyle, width: '120px', borderColor: '#fdba74', fontWeight: 'bold'}} />
            <strong style={{ color: '#9a3412' }}>%</strong>
          </div>
          <small style={{ color: '#9a3412', display: 'block', marginTop: '5px' }}>Por encima de este porcentaje, el cajero deberá ingresar usuario y PIN de un administrador.</small>
        </div>

        <div style={{ marginTop: '10px', padding: '15px', backgroundColor: config.afip_configurado ? '#f0fdf4' : '#fff7ed', borderRadius: '8px', border: `1px solid ${config.afip_configurado ? '#bbf7d0' : '#fed7aa'}` }}>
          <h4 style={{ margin: '0 0 8px 0', color: config.afip_configurado ? '#166534' : '#9a3412' }}>🔐 Certificados Digitales ARCA</h4>
          <p style={{ margin: 0, fontSize: '13px', color: '#475569' }}>
            Estado: <strong>{config.afip_configurado ? 'Configurados en el servidor' : 'No configurados'}</strong> · Ambiente: <strong>{config.afip_ambiente}</strong>
          </p>
          <small style={{ display: 'block', marginTop: '8px', color: '#64748b' }}>
            Por seguridad, las rutas y claves se configuran únicamente en el archivo .env del servidor y no se guardan desde esta pantalla.
          </small>
        </div>

        {/* ── MERCADO PAGO ── */}
        <div style={{ marginTop: '10px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
          <h4 style={{ margin: '0 0 12px 0', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>💙</span> Mercado Pago (QR Estático)
          </h4>
          <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#0369a1' }}>
            Estos datos se muestran en la pantalla de cobro cuando el cajero elige "Mercado Pago". El QR lo imprimís desde la app de MP.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Alias MP</label>
              <input type="text" name="mp_alias" value={config.mp_alias} onChange={handleChange} style={inputStyle} placeholder="Ej: kiosco.elverde.mp" />
            </div>
            <div>
              <label style={labelStyle}>CBU/CVU de MP</label>
              <input type="text" name="mp_cbu" value={config.mp_cbu} onChange={handleChange} style={inputStyle} placeholder="22 dígitos" maxLength={22} />
            </div>
          </div>
          <div style={{ marginTop: '12px' }}>
            <label style={labelStyle}>Titular de la cuenta MP</label>
            <input type="text" name="mp_titular" value={config.mp_titular} onChange={handleChange} style={inputStyle} placeholder="Nombre y apellido del titular" />
          </div>
          <div style={{ marginTop: '12px' }}>
            <label style={labelStyle}>URL de imagen del QR (opcional)</label>
            <input type="text" name="mp_qr_imagen_url" value={config.mp_qr_imagen_url} onChange={handleChange} style={inputStyle} placeholder="https://... o ruta local al PNG del QR" />
            <small style={{ color: '#0369a1', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              Si dejás este campo vacío, en la pantalla de cobro solo se mostrará el alias y CBU en texto.
            </small>
          </div>
        </div>

        {/* ── TRANSFERENCIA BANCARIA ── */}
        <div style={{ marginTop: '10px', padding: '15px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
          <h4 style={{ margin: '0 0 12px 0', color: '#15803d', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>🏦</span> Transferencia Bancaria
          </h4>
          <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#15803d' }}>
            Estos datos se muestran cuando el cliente paga por Modo, banco o cualquier transferencia directa.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Alias bancario</label>
              <input type="text" name="banco_alias" value={config.banco_alias} onChange={handleChange} style={inputStyle} placeholder="Ej: KIOSCO.EL.VERDE" />
            </div>
            <div>
              <label style={labelStyle}>Banco / Billetera</label>
              <input type="text" name="banco_nombre" value={config.banco_nombre} onChange={handleChange} style={inputStyle} placeholder="Ej: Banco Galicia, Brubank..." />
            </div>
          </div>
          <div style={{ marginTop: '12px' }}>
            <label style={labelStyle}>CBU / CVU</label>
            <input type="text" name="banco_cbu" value={config.banco_cbu} onChange={handleChange} style={inputStyle} placeholder="22 dígitos" maxLength={22} />
          </div>
          <div style={{ marginTop: '12px' }}>
            <label style={labelStyle}>Titular de la cuenta</label>
            <input type="text" name="banco_titular" value={config.banco_titular} onChange={handleChange} style={inputStyle} placeholder="Nombre y apellido del titular" />
          </div>
        </div>
      </div>

      <button onClick={guardarConfiguracion} style={{ width: '100%', padding: '15px', backgroundColor: '#3b82f6', color: 'white', fontWeight: 'bold', fontSize: '18px', border: 'none', borderRadius: '8px', cursor: 'pointer', marginTop: '25px' }}>
        💾 Guardar Configuración
      </button>
    </div>
  );
}

const labelStyle = { display: 'block', fontWeight: 'bold', marginBottom: '6px', color: '#475569', fontSize: '13px' };
const inputStyle = { width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '15px', boxSizing: 'border-box' };